<template>
    <read-only-zone-observation v-if="!isAdmin"></read-only-zone-observation>
    <div v-else class="flex items-center flex-col w-full">
        <ObservationForm @deleted="back" ref="form" :saveFn="save" :id="$route.params.observationId"></ObservationForm>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
        <app-multi-picker
            ref="multiZonePicker"
            class="col-span-2"
            variant="hidden"
            :show-label="false"
            v-model="zonesToCopy"
            :options="zones"
            :title="$t('observations.duplicateByZone')"
        >
            <template v-slot:option="{ option }">
                <span class="mr-2 p-2">
                    {{ option.fullName }}
                </span>
            </template>
            <template v-slot:footer>
                <div class="p-4 flex justify-end">
                    <app-button
                        @click="duplicateByZone"
                        :disabled="zonesToCopy.length === 0"
                        variant="primary"
                        :label="$t('observations.duplicateByZoneValidation')"
                    ></app-button>
                </div>
            </template>
        </app-multi-picker>
    </div>
</template>

<script>
import { createObservation, sanitizeObservation, updateObservation } from '@/features/observations/observation.service';
import ObservationForm from '@/features/observations/ObservationForm';
import ReadOnlyZoneObservation from '@/features/tours/ReadOnlyZoneObservation';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import { queryProject } from '@/features/projects/projects.service';
import AppButton from '@/components/appButton/AppButton.vue';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker.vue';
import AppBundle from '@/components/app-bundle/appBundle.vue';
import { info } from '@/features/toasts/toats.service';
import { getLocationsTree, sortByFullName } from '@/features/locations/locations.service';

export default {
    components: { AppBundle, AppMultiPicker, AppButton, AppQuickActions, ReadOnlyZoneObservation, ObservationForm },
    created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.isAdmin = project.me.allowedFeatures.includes('project_observations');
            this.projectPhase = project.phase;
            this.defaultReporterId = project.me.bundleIds[0];
        });

        this.subscriptions = [
            getLocationsTree(this.$route.params.projectId).subscribe((folders) => {
                this.zones = sortByFullName(
                    folders
                        .reduce((acc, folder) => [...acc, ...folder.locations], [])
                        .reduce((acc, location) => [...acc, ...location.zones], []),
                );
            }),
        ];
        localStorage.setItem(
            'observation.lastVisitedObservationId.' + this.$route.params.projectId,
            this.$route.params.observationId,
        );
    },
    computed: {
        quickActions() {
            if (this.$route.params.observationId === 'new') {
                return [];
            } else {
                return [
                    {
                        name: this.$t('observations.duplicateByZone'),
                        run: () => this.$refs.multiZonePicker.open(),
                    },
                    {
                        name: this.$t('observations.duplicate'),
                        run: () => this.duplicate(),
                    },
                    {
                        name: this.$t('observations.newObservation'),
                        run: () => this.newObservation(),
                    },
                ];
            }
        },
    },
    methods: {
        async duplicateByZone() {
            this.$refs.multiZonePicker.close();
            await Promise.all(
                this.zonesToCopy.map((zone) => {
                    const currentObservation = this.$refs.form.observation;
                    const observationEntity = {
                        title: currentObservation.title,
                        phase: currentObservation.phase || this.safePhase(),
                        dueDate: currentObservation.dueDate,
                        zoneId: zone.id,
                        type: currentObservation.type,
                        theme: currentObservation.theme,
                        reportedBy: currentObservation.reporter ? currentObservation.reporter.id : null,
                        recipientIds: currentObservation.recipientIds || [],
                    };
                    return createObservation(this.$route.params.projectId, observationEntity);
                }),
            );
            info(this.zonesToCopy.length + ' ' + this.$t('observations.duplicationSucceed'), 5000);
            this.zonesToCopy = [];
        },
        async newObservation() {
            const observation = await createObservation(this.$route.params.projectId, {
                title: '',
                phase: this.projectPhase,
                reportedBy: this.defaultReporterId,
            });
            await this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    observationId: observation.id,
                },
            });
        },
        async duplicate() {
            const currentObservation = this.$refs.form.observation;
            const observationEntity = {
                title: currentObservation.title,
                page: currentObservation.page || 1,
                footprint: currentObservation.footprint || null,
                phase: currentObservation.phase || this.safePhase(),
                dueDate: currentObservation.dueDate,
                zoneId: currentObservation.zoneId,
                roomId: currentObservation.roomId,
                supportId: currentObservation.supportId,
                type: currentObservation.type,
                reportedBy: currentObservation.reporter ? currentObservation.reporter.id : null,
                reportedAt: currentObservation.reportedAt,
                recipientIds: currentObservation.recipientIds || [],
            };
            const observation = await createObservation(this.$route.params.projectId, observationEntity);
            this.$router.push({
                name: this.$route.name,
                params: {
                    projectId: this.$route.params.projectId,
                    observationId: observation.id,
                },
            });
        },
        async save(observation) {
            const observationEntity = sanitizeObservation(observation);
            return updateObservation(this.$route.params.projectId, { id: observation.id, ...observationEntity });
        },
        back() {
            this.$router.push({ name: this.$route.meta.parentName, params: this.$route.params });
        },
    },
    data() {
        return { isAdmin: false, defaultReporterId: null, projectPhase: null, zonesToCopy: [], zones: [] };
    },
};
</script>
